/**
 * HarmonyEngine — reference-grade in-browser vocal harmony generator.
 *
 * Takes ONE dry solo-vocal AudioBuffer and synthesizes musically-correct
 * harmony voices from it, keeping the singer's OWN timbre (DSP from their
 * recording — not an AI voice swap). Composes only license-clean, self-hosted
 * modules already shipped by Timbrica:
 *
 *   - window.AudioPipeline.stretch / SignalsmithWorkerEngine.shift
 *       Signalsmith Stretch 1.3.2 (MIT) formant-preserving pitch shift.
 *   - window.PitchTracker.track (YIN) — per-note segments for diatonic harmony.
 *   - window.__safeAudioBuffer — OOM-safe AudioBuffer allocation.
 *
 * What makes it an etalon vs a naive detune-and-stack (the free-tool trap):
 *   1. DIATONIC intervals — a "third above" is +3 or +4 semitones depending on
 *      the scale degree, so the harmony stays in key (chromatic harmonizers
 *      sound wrong on some notes).
 *   2. HUMANIZATION / DECORRELATION — layered copies of the SAME voice comb-
 *      filter into a hollow, robotic sound. Each voice gets its own detune
 *      (cents), micro-timing (ms, < Haas 30ms), formant offset and pan so the
 *      stack sounds like distinct singers, not a phaser.
 *   3. VOICED GATING — harmony is silent over consonants/breath/gaps; pitching
 *      unvoiced frames produces artifacted noise. This is the single biggest
 *      quality lever.
 *   4. FORMANT PRESERVATION always on — no chipmunk on up-shifts, no "demon"
 *      on down-shifts; independent per-voice formant nudges fake distinct
 *      singers cheaply.
 *
 * Public API (window.HarmonyEngine):
 *   .SCALES, .KEYS, .PRESETS
 *   .detectKey(buffer) -> {tonic:0..11, mode:'major'|'minor', name:'C'|'Am'}
 *   async .analyze(buffer, {onProgress}) -> analysis {segments, duration, sampleRate, key...}
 *   .buildVoicePlan(analysis, cfg) -> {voices:[voicePlan...], cfg}
 *   async .render(sourceBuffer, cfg, {analysis, onProgress, signal}) ->
 *          {voices:[{buffer, params, label}], analysis}
 *   .buildVoiceChain(ctx, buffer, params) -> {output} (used by BOTH offline mix & live preview)
 *   async .mixToBuffer(dryBuffer, voices, opts) -> mixed AudioBuffer
 */
(function () {
'use strict';

/* ============================ constants ============================ */

var KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Scale interval sets — semitone offsets from the tonic (one octave).
var SCALES = {
    'major':          [0, 2, 4, 5, 7, 9, 11],
    'natural_minor':  [0, 2, 3, 5, 7, 8, 10],
    'harmonic_minor': [0, 2, 3, 5, 7, 8, 11],
    'dorian':         [0, 2, 3, 5, 7, 9, 10],
    'mixolydian':     [0, 2, 4, 5, 7, 9, 10],
    'major_pentatonic': [0, 2, 4, 7, 9],
    'minor_pentatonic': [0, 3, 5, 7, 10]
};

// Krumhansl-Kessler key profiles (for chroma correlation key detection).
var MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
var MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

// Voicing presets. Each voice: {label, degree?|fixed?, gainDb, pan, formant}.
//   degree  = diatonic scale steps from the lead (2 = a "third", 4 = a "fifth",
//             5 = a "sixth", 7|scale.length = an octave). Stays in the chosen key.
//   fixed   = fixed chromatic semitone offset (ignores scale; for octaves/effects).
//   gainDb  = base level relative to the lead.
//   pan     = base stereo position (-1 L .. +1 R); low voices are pulled center.
//   formant = base formant nudge (semitones) to differentiate the singer's timbre.
// `space` (reverb glue 0..1) and `width` (stereo spread multiplier) are the
// preset's mix defaults — a "genre pack": Pop is tight and dry, Gospel/Choir are
// lush and wide. The blade applies them to the Space/Width sliders on select.
var PRESETS = [
    {
        id: 'double', humanize: 0.5, wet: 0.85, space: 0.10, width: 1.10,
        voices: [
            { label: 'Double L', fixed: 0, gainDb: -2.5, pan: -0.4, formant: 0.15 },
            { label: 'Double R', fixed: 0, gainDb: -2.5, pan: 0.4, formant: -0.15 }
        ]
    },
    {
        id: 'duet', humanize: 0.4, wet: 0.85, space: 0.15, width: 1.0,
        voices: [
            { label: '3rd above', degree: 2, gainDb: -3, pan: 0.28, formant: 0 }
        ]
    },
    {
        id: 'trio', humanize: 0.42, wet: 0.85, space: 0.18, width: 1.05, isDefault: true,
        voices: [
            { label: '3rd above', degree: 2, gainDb: -3, pan: -0.32, formant: 0 },
            { label: '5th above', degree: 4, gainDb: -4, pan: 0.32, formant: -0.2 }
        ]
    },
    {
        // Modern pop stack: two humanized doubles + a quiet third. Tight and dry.
        id: 'pop', humanize: 0.30, wet: 0.75, space: 0.06, width: 1.15,
        voices: [
            { label: 'Double L', fixed: 0, gainDb: -3, pan: -0.45, formant: 0.1 },
            { label: 'Double R', fixed: 0, gainDb: -3, pan: 0.45, formant: -0.1 },
            { label: '3rd above', degree: 2, gainDb: -7, pan: 0.2, formant: -0.15 }
        ]
    },
    {
        // R&B stack: sweet 3rd + 6th above over a warm octave-down. Lush, wide.
        id: 'rnb', humanize: 0.55, wet: 0.80, space: 0.32, width: 1.25,
        voices: [
            { label: '3rd above', degree: 2, gainDb: -4, pan: -0.3, formant: 0 },
            { label: '6th above', degree: 5, gainDb: -6, pan: 0.34, formant: -0.35 },
            { label: 'Octave down', fixed: -12, gainDb: -5, pan: 0, formant: 1.2 }
        ]
    },
    {
        id: 'octaves', humanize: 0.35, wet: 0.9, space: 0.12, width: 0.90,
        voices: [
            { label: 'Octave down', fixed: -12, gainDb: -3, pan: -0.18, formant: 1.4 },
            { label: 'Octave up', fixed: 12, gainDb: -4.5, pan: 0.18, formant: -1.2 }
        ]
    },
    {
        id: 'quartet', humanize: 0.45, wet: 0.82, space: 0.20, width: 1.0,
        voices: [
            { label: 'Bass (8vb)', fixed: -12, gainDb: -3, pan: 0, formant: 1.6 },
            { label: '3rd above', degree: 2, gainDb: -4, pan: -0.36, formant: 0 },
            { label: '5th above', degree: 4, gainDb: -5, pan: 0.36, formant: -0.3 }
        ]
    },
    {
        id: 'gospel', humanize: 0.6, wet: 0.8, space: 0.38, width: 1.30,
        voices: [
            { label: '3rd above', degree: 2, gainDb: -4, pan: -0.24, formant: 0 },
            { label: '5th above', degree: 4, gainDb: -5, pan: 0.24, formant: -0.25 },
            { label: '6th above', degree: 5, gainDb: -6, pan: -0.46, formant: -0.4 },
            { label: '3rd below', degree: -2, gainDb: -5, pan: 0.46, formant: 0.5 }
        ]
    },
    {
        id: 'choir', humanize: 0.85, wet: 0.78, space: 0.50, width: 1.45, choir: true, choirVoices: 10
        // voices are generated procedurally in buildVoicePlan (see below).
    }
];

/* ============================ math helpers ============================ */

function freqToMidi(hz) { return 12 * Math.log2(hz / 440) + 69; }
function midiToFreq(m) { return 440 * Math.pow(2, (m - 69) / 12); }
function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
function dbToGain(db) { return Math.pow(10, db / 20); }

// Deterministic PRNG so a given (seed, voiceIndex) always humanizes identically
// — reproducible renders (and testable). mulberry32 (public domain).
function mulberry32(a) {
    return function () {
        a |= 0; a = (a + 0x6D2B79F5) | 0;
        var t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// Absolute scale-step index of the in-scale note nearest `midi`, anchored at
// `tonic` (0..11). Handles octave + pitch-class wrap.
function snapIndex(midi, tonic, scale) {
    var n = scale.length;
    var rel = midi - tonic;
    var oct = Math.floor(rel / 12);
    var pc = rel - oct * 12; // 0..12
    var bestI = 0, bestD = Infinity, bestWrap = 0;
    for (var i = 0; i < n; i++) {
        var d = Math.abs(pc - scale[i]);
        if (d < bestD) { bestD = d; bestI = i; bestWrap = 0; }
        var dw = Math.abs(pc - (scale[i] + 12));
        if (dw < bestD) { bestD = dw; bestI = i; bestWrap = 1; }
    }
    return (oct + bestWrap) * n + bestI;
}

function midiFromScaleIndex(absIdx, tonic, scale) {
    var n = scale.length;
    var oct = Math.floor(absIdx / n);
    var i = absIdx - oct * n;
    return tonic + oct * 12 + scale[i];
}

// Diatonic transpose: move `degrees` scale-steps from `midi` inside `scale`
// anchored at `tonic`. degrees=+2 -> a diatonic third (yields +3 or +4
// semitones depending on where the note sits in the scale). This is the
// intelligence chromatic harmonizers lack.
function diatonicTranspose(midi, degrees, tonic, scale) {
    var absIdx = snapIndex(midi, tonic, scale);
    return midiFromScaleIndex(absIdx + degrees, tonic, scale);
}

// Constant chromatic interval for a diatonic degree, using the major scale as
// the reference (degree 2 -> +4 major third, 4 -> +7 fifth, 5 -> +9 sixth,
// 7 -> +12 octave). Used by "fixed interval" mode so preset degrees still map
// to a sensible constant offset.
function degreeToFixedSemitones(degree) {
    var maj = [0, 2, 4, 5, 7, 9, 11], n = 7;
    var oct = Math.floor(degree / n);
    var i = ((degree - oct * n) % n + n) % n;
    return oct * 12 + maj[i];
}

/* ============================ buffer helpers ============================ */

function makeBuffer(channels, length, sr) {
    length = Math.max(1, length | 0);
    if (window.__safeAudioBuffer) {
        return window.__safeAudioBuffer({ numberOfChannels: channels, length: length, sampleRate: sr });
    }
    var oc = new OfflineAudioContext(channels, length, sr);
    return oc.createBuffer(channels, length, sr);
}

// Downmix any AudioBuffer to a fresh mono AudioBuffer (harmony voices are mono;
// they get panned in the mix stage — avoids stereo phase issues on the shift).
function downmixMono(buffer) {
    var len = buffer.length, ch = buffer.numberOfChannels;
    var out = makeBuffer(1, len, buffer.sampleRate);
    var d = out.getChannelData(0);
    if (ch === 1) { d.set(buffer.getChannelData(0)); return out; }
    for (var c = 0; c < ch; c++) {
        var src = buffer.getChannelData(c);
        for (var i = 0; i < len; i++) d[i] += src[i] / ch;
    }
    return out;
}

/* ============================ key detection ============================ */

function pearson(chroma, shift, profile) {
    var n = 12, sx = 0, sy = 0, sxy = 0, sx2 = 0, sy2 = 0;
    for (var i = 0; i < n; i++) {
        var x = chroma[(i + shift) % 12], y = profile[i];
        sx += x; sy += y; sxy += x * y; sx2 += x * x; sy2 += y * y;
    }
    var num = n * sxy - sx * sy;
    var den = Math.sqrt((n * sx2 - sx * sx) * (n * sy2 - sy * sy));
    return den === 0 ? 0 : num / den;
}

// Krumhansl-Schmuckler key detection from raw audio (Goertzel chroma over
// octaves 1..7, Hann-windowed chunks). Returns {tonic, mode, name}. Forked
// from the /pitch etalon's detectKey (proven on real vocals).
function detectKey(buffer) {
    try {
        var sr = buffer.sampleRate;
        var raw = buffer.getChannelData(0);
        var fftSize = 8192;
        var chroma = new Float64Array(12);
        var numChunks = 20;
        var positions = [];
        for (var i = 0; i < numChunks; i++) {
            var pos = Math.floor(i * raw.length / numChunks);
            if (pos + fftSize <= raw.length) positions.push(pos);
        }
        for (var ci = 0; ci < positions.length; ci++) {
            var re = new Float64Array(fftSize);
            var base = positions[ci];
            for (var s = 0; s < fftSize; s++) {
                re[s] = raw[base + s] * (0.5 - 0.5 * Math.cos(2 * Math.PI * s / fftSize)); // Hann
            }
            for (var note = 0; note < 12; note++) {
                var energy = 0;
                for (var octave = 1; octave <= 7; octave++) {
                    var freq = 440 * Math.pow(2, (note - 9) / 12 + (octave - 4));
                    if (freq > sr / 2) break;
                    var k = Math.round(freq * fftSize / sr);
                    var w = 2 * Math.PI * k / fftSize;
                    var coeff = 2 * Math.cos(w);
                    var s1 = 0, s2 = 0, s0;
                    for (var nn = 0; nn < fftSize; nn++) { s0 = re[nn] + coeff * s1 - s2; s2 = s1; s1 = s0; }
                    energy += s1 * s1 + s2 * s2 - coeff * s1 * s2;
                }
                chroma[note] += energy;
            }
        }
        var maxC = 0, j;
        for (j = 0; j < 12; j++) if (chroma[j] > maxC) maxC = chroma[j];
        if (maxC > 0) for (j = 0; j < 12; j++) chroma[j] /= maxC;
        var bestCorr = -2, bestKey = 0, bestMode = 'major';
        for (var shift = 0; shift < 12; shift++) {
            var cM = pearson(chroma, shift, MAJOR_PROFILE);
            var cm = pearson(chroma, shift, MINOR_PROFILE);
            if (cM > bestCorr) { bestCorr = cM; bestKey = shift; bestMode = 'major'; }
            if (cm > bestCorr) { bestCorr = cm; bestKey = shift; bestMode = 'minor'; }
        }
        return {
            tonic: bestKey,
            mode: bestMode,
            name: KEYS[bestKey] + (bestMode === 'minor' ? 'm' : '')
        };
    } catch (e) {
        return { tonic: 0, mode: 'major', name: 'C' };
    }
}

// Key from the MELODY the tracker already found, not from raw spectrum.
//
// detectKey() above sums spectral energy per pitch class across octaves 1-7.
// That is a chroma of the whole signal, and a voice's spectrum is dominated by
// its FORMANTS — resonances that sit at fixed frequencies no matter what is
// being sung. Measured 2026-08-29 on a source-filter voice (F1 700 Hz, i.e.
// F5 = 698.46 Hz) singing plain scales:
//
//   C major scale  C D E F G A B C  -> "F major"
//   A natural minor A B C D E F G A -> "F major"
//   G major        G A B C D E F# G -> "F# minor"
//
// Two different scales answering "F" is the formant voting, not the melody: F1
// simply parks energy on one pitch class for the whole take. And the key is not
// cosmetic here — every diatonic interval is derived from it, so a wrong key
// puts every harmony note on the wrong scale degree.
//
// The tracker has already produced a per-frame pitch. Weighting pitch CLASSES by
// how long they are actually sung and correlating that against the same
// Krumhansl-Kessler profiles uses the information we already paid for, and a
// formant cannot vote at all. Same method as detectKey(notes) in
// audio-to-notes-renderer.js, which has been doing it this way all along.
function keyFromFrames(frames, hopSec) {
    if (!frames || !frames.length) return null;
    var pc = new Float64Array(12);
    var voicedSec = 0;
    for (var i = 0; i < frames.length; i++) {
        var f = frames[i] && frames[i].freqHz;
        if (!f || f <= 0) continue;
        var midi = Math.round(12 * Math.log2(f / 440) + 69);
        if (!isFinite(midi)) continue;
        var w = hopSec;
        pc[((midi % 12) + 12) % 12] += w;
        voicedSec += w;
    }
    // Too little sung material to out-vote noise: let the spectral method answer.
    if (voicedSec < 0.6) return null;
    var maxC = 0, j;
    for (j = 0; j < 12; j++) if (pc[j] > maxC) maxC = pc[j];
    if (maxC <= 0) return null;
    for (j = 0; j < 12; j++) pc[j] /= maxC;

    var bestCorr = -2, bestKey = 0, bestMode = 'major';
    for (var shift = 0; shift < 12; shift++) {
        var cM = pearson(pc, shift, MAJOR_PROFILE);
        var cm = pearson(pc, shift, MINOR_PROFILE);
        if (cM > bestCorr) { bestCorr = cM; bestKey = shift; bestMode = 'major'; }
        if (cm > bestCorr) { bestCorr = cm; bestKey = shift; bestMode = 'minor'; }
    }
    return {
        tonic: bestKey,
        mode: bestMode,
        name: KEYS[bestKey] + (bestMode === 'minor' ? 'm' : ''),
        source: 'melody'
    };
}

/* ===================== solo-vocal vs full mix ====================== */

// Share of the take's energy that lives below 160 Hz, over a 30 Hz high-pass.
//
// This is the "did you upload a whole song" signal. It used to be inferred from
// the voicing gate ("almost nothing is trackable, yet the file is loud, so it
// must be a mix") — which only worked while the gate was broken for EVERY vocal,
// and went silent the moment the gate was fixed. It is also the wrong question:
// a full mix is not "hard to track", it is "has a rhythm section under the
// singer". So ask that directly.
//
// Register-robust, because a sung voice puts most of its energy in the formant
// region, not in its fundamental. Measured 2026-08-22: solo takes (female,
// tenor, and a bass whose median f0 is 100 Hz, dry and reverberant) land at
// 0-2%; nine full mixes land at 27-65%. The threshold lives in the page.
function lowBandEnergyRatio(mono) {
    var d = mono.getChannelData(0), sr = mono.sampleRate;
    function biquad(type, f0, Q) {
        var w0 = 2 * Math.PI * f0 / sr, c = Math.cos(w0), sn = Math.sin(w0), al = sn / (2 * Q);
        var b0, b1, b2, a0, a1, a2;
        if (type === 'lp') { b0 = (1 - c) / 2; b1 = 1 - c; b2 = b0; }
        else { b0 = (1 + c) / 2; b1 = -(1 + c); b2 = b0; }
        a0 = 1 + al; a1 = -2 * c; a2 = 1 - al;
        b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;
        var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
        return function (x) {
            var y = b0 * x + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
            x2 = x1; x1 = x; y2 = y1; y1 = y; return y;
        };
    }
    var hpA = biquad('hp', 30, 0.707), hpB = biquad('hp', 30, 0.707);
    var lpA = biquad('lp', 160, 0.707), lpB = biquad('lp', 160, 0.707);
    var eLow = 0, eTot = 0;
    for (var i = 0; i < d.length; i++) {
        var x = hpB(hpA(d[i]));
        var l = lpB(lpA(x));
        eTot += x * x; eLow += l * l;
    }
    return eTot > 0 ? eLow / eTot : 0;
}

/* ======================= voicing detection ========================= */

// Two DIFFERENT questions get asked of the tracker, and conflating them is what
// made this tool inaudible on real singing:
//
//   "what pitch is being sung here"  -> note segments (only the diatonic
//                                       voices need this)
//   "is a voice sounding here"       -> the GATE that decides whether ANY
//                                       harmony is heard at all
//
// Until 2026-08-22 both came from PitchTracker's built-in note segmenter run at
// instrument thresholds (CMND 0.15 / minConf 0.85 / split on a 50-cent jump /
// drop under 80 ms, then again under 120 ms in buildVoicePlan). Real singing has
// vibrato of +-50..150 cents at 5-6 Hz, reverb tails and breathy onsets, so that
// segmenter shreds sustained notes into sub-80 ms fragments and then drops them
// -- and every dropped fragment is SILENCE in the harmony. Measured on a real
// separated 40 s a-cappella: the gate was open 2.6 s (7% of the file, 16
// fragments averaging 0.16 s), i.e. the choir was a handful of blips. The same
// finding was already measured for /audio-to-notes on 2026-07-16 and fixed there
// (__atnClean.segmentVocalFrames); it simply never reached this tool.
//
// So the gate is now its own thing: frame-level voicing with confidence
// hysteresis + an energy floor, gap bridging and a short release. Numbers below
// are measured (tests/playwright/harmony-voicing-gate.mjs), not guessed.
var VOICE_ENTER_CONF   = 0.70;   // YIN confidence needed to OPEN the gate
var VOICE_SUSTAIN_CONF = 0.50;   // ...and to keep it open (soft tails/onsets)
var VOICE_FLOOR_DB     = -26;    // frame RMS floor, relative to the take's p95
var VOICE_BRIDGE_S     = 0.10;   // close gaps this short (consonant / dropout)
var VOICE_MIN_S        = 0.08;   // discard anything shorter than a syllable
var VOICE_TAIL_S       = 0.06;   // release, so words do not cut off dead

// Per-frame RMS on the tracker's 10 ms grid, plus the take's 95th-percentile
// level. Block sums keep this O(n) time / O(frames) memory on long files.
function frameLevels(mono, frames, hopSec) {
    var d = mono.getChannelData(0), sr = mono.sampleRate;
    var blk = Math.max(1, Math.round(hopSec * sr));
    var nB = Math.max(1, Math.floor(d.length / blk));
    var e = new Float64Array(nB);
    for (var b = 0; b < nB; b++) {
        var s = 0, o = b * blk;
        for (var i = 0; i < blk; i++) { var v = d[o + i]; s += v * v; }
        e[b] = s;
    }
    var n = frames.length;
    var rms = new Float32Array(n);
    for (var f = 0; f < n; f++) {
        var bi = Math.min(nB - 1, Math.max(0, Math.round(frames[f].timeS * sr / blk)));
        var b2 = Math.min(nB - 1, bi + 1);         // 20 ms window, clamped at the end
        rms[f] = Math.sqrt((e[bi] + e[b2]) / (2 * blk));
    }
    var sorted = Array.prototype.slice.call(rms).sort(function (a, b2) { return a - b2; });
    return { rms: rms, p95: sorted[Math.floor(n * 0.95)] || 0 };
}

// Frame voicing -> merged regions [{startS,endS}] the harmony may sound in.
function detectVoicedRegions(frames, mono, hopSec) {
    var n = frames.length;
    if (!n) return [];
    hopSec = hopSec || 0.010;
    var lv = frameLevels(mono, frames, hopSec);
    var floor = lv.p95 * Math.pow(10, VOICE_FLOOR_DB / 20);
    var on = new Uint8Array(n);
    var open = false;
    var i, j, k;
    for (i = 0; i < n; i++) {
        var fr = frames[i];
        var ok = fr.freqHz > 0
              && fr.confidence >= (open ? VOICE_SUSTAIN_CONF : VOICE_ENTER_CONF)
              && lv.rms[i] >= floor;
        on[i] = ok ? 1 : 0;
        open = ok;
    }
    var bridge = Math.round(VOICE_BRIDGE_S / hopSec);
    for (i = 0; i < n; ) {                                   // close short gaps
        if (on[i]) { i++; continue; }
        for (j = i; j < n && !on[j]; j++) {}
        if (i > 0 && j < n && (j - i) <= bridge) for (k = i; k < j; k++) on[k] = 1;
        i = j;
    }
    var minF = Math.round(VOICE_MIN_S / hopSec);
    for (i = 0; i < n; ) {                                   // drop sub-syllable blips
        if (!on[i]) { i++; continue; }
        for (j = i; j < n && on[j]; j++) {}
        if ((j - i) < minF) for (k = i; k < j; k++) on[k] = 0;
        i = j;
    }
    var regions = [], dur = mono.duration;
    for (i = 0; i < n; ) {
        if (!on[i]) { i++; continue; }
        for (j = i; j < n && on[j]; j++) {}
        regions.push({
            startS: frames[i].timeS,
            endS: Math.min(dur, frames[j - 1].timeS + hopSec + VOICE_TAIL_S)
        });
        i = j;
    }
    for (i = 1; i < regions.length; i++) {                   // a release may overlap
        if (regions[i].startS < regions[i - 1].endS) regions[i - 1].endS = regions[i].startS;
    }
    return regions.filter(function (r) { return r.endS > r.startS; });
}

// Note segments (the PITCH answer). The vocal-tuned segmenter proven on
// /audio-to-notes is the source of truth; the fallback below only has to keep
// the diatonic presets alive if that module failed to load -- the fixed/octave/
// choir presets never consult notes at all.
function noteSegments(frames, voiced) {
    try {
        if (window.__atnClean && window.__atnClean.segmentVocalFrames) {
            var s = window.__atnClean.segmentVocalFrames(frames, {});
            if (s && s.length) return s;
        }
    } catch (_) {}
    var out = [];
    for (var r = 0; r < voiced.length; r++) {
        var hz = [];
        for (var i = 0; i < frames.length; i++) {
            var f = frames[i];
            if (f.timeS < voiced[r].startS) continue;
            if (f.timeS > voiced[r].endS) break;
            if (f.freqHz > 0) hz.push(f.freqHz);
        }
        if (!hz.length) continue;
        hz.sort(function (a, b) { return a - b; });
        out.push({ startS: voiced[r].startS, endS: voiced[r].endS,
                   medianFreqHz: hz[hz.length >> 1], frames: hz.length });
    }
    return out;
}

/* ============================ analysis ============================ */

// Track the melody (YIN) and detect the key. Runs ONCE per file; buildVoicePlan
// + render reuse it so param tweaks re-render without re-tracking.
async function analyze(buffer, opts) {
    opts = opts || {};
    var onp = opts.onProgress || function () {};
    var mono = downmixMono(buffer);
    onp(0.1, 'tracking');

    var HOP = 0.010;
    var tracking = await window.PitchTracker.track(mono, {
        hopSec: HOP,
        windowSec: 0.040,
        minFreqHz: 65,
        // A sung fundamental tops out around C6 (1046 Hz) even for a soprano
        // belt, so anything above this is YIN locking onto a harmonic, not a
        // note. Letting those through is not neutral: an octave lock is harmless
        // (the pitch CLASS, and therefore the diatonic interval, is unchanged),
        // but a 3rd-harmonic lock is +19 semitones and turns a third into a
        // fourth. Measured on a real separated stem: 46% of frames landed above
        // 1100 Hz at the shipped setting; capping here takes that to 0% and
        // costs ~6% of the voiced frames.
        maxFreqHz: 1100,
        // Vocal thresholds, not instrument ones: CMND 0.45 lets reverberant and
        // breathy frames through WITH their confidence, and minConf is opened up
        // so the voicing decision happens in detectVoicedRegions (hysteresis)
        // instead of being pre-decided per frame. Measured 2026-07-16 on a real
        // stem: raw voiced frames 33% -> 64% at this threshold.
        cmndThreshold: 0.45,
        minConf: 0.01,
        minSegmentMs: 80,
        onProgress: function (p) { onp(0.1 + p * 0.7, 'tracking'); }
    });

    var frames = tracking.frames || [];
    var voiced = detectVoicedRegions(frames, mono, HOP);

    onp(0.85, 'key');
    // Melody first, spectrum only as the fallback — see keyFromFrames().
    var key = keyFromFrames(frames, HOP) || detectKey(mono);
    var segs = noteSegments(frames, voiced);
    var lowBandRatio = lowBandEnergyRatio(mono);

    // Polyphony hint — warn if the input looks like a full mix, not a solo vocal.
    var poly = 1;
    try {
        if (window.__atnClean && window.__atnClean.yinSegmentsToNotes && window.__atnClean.polyphony) {
            var notes = window.__atnClean.yinSegmentsToNotes(segs);
            poly = window.__atnClean.polyphony(notes) || 1;
        }
    } catch (_) {}

    // Voiced coverage — fraction of the file the harmony is allowed to sound in.
    // This is the GATE's own total, not the note segments': it is what the page
    // shows as "no singing detected", and it has to agree with what gets heard.
    var voicedSec = 0;
    for (var i = 0; i < voiced.length; i++) voicedSec += (voiced[i].endS - voiced[i].startS);

    onp(1, 'done');
    return {
        segments: segs,
        voiced: voiced,
        frames: frames,
        duration: buffer.duration,
        sampleRate: buffer.sampleRate,
        channels: buffer.numberOfChannels,
        key: key,
        polyphony: poly,
        lowBandRatio: lowBandRatio,
        voicedSec: voicedSec,
        voicedRatio: buffer.duration > 0 ? voicedSec / buffer.duration : 0
    };
}

/* ============================ voice planning ============================ */

// Cut every voiced region into contiguous tiles, one per note sounding inside
// it, so the region is covered end to end with no seams. A tile boundary sits
// at the midpoint of the silence between two notes; a region with no trackable
// note at all still gets one tile (using the nearest note's pitch), because a
// gate that is open must never render silence. `shiftFor(noteMidi)` returns the
// semitones for that tile.
function tileRegions(voiced, notes, shiftFor) {
    var tiles = [];
    if (!voiced.length) return tiles;
    var ni = 0;
    for (var r = 0; r < voiced.length; r++) {
        var reg = voiced[r];
        while (ni > 0 && notes[ni - 1] && notes[ni - 1].endS > reg.startS) ni--;
        var inside = [];
        for (var k = ni; k < notes.length; k++) {
            var nn = notes[k];
            if (nn.endS <= reg.startS) { ni = k + 1; continue; }
            if (nn.startS >= reg.endS) break;
            inside.push(nn);
        }
        if (!inside.length) {
            var near = nearestNote(notes, (reg.startS + reg.endS) / 2);
            if (!near) continue;                       // nothing anywhere to take a pitch from
            tiles.push({ startS: reg.startS, endS: reg.endS,
                         shift: shiftFor(freqToMidi(near.medianFreqHz)), head: true, tail: true });
            continue;
        }
        var local = [];
        for (var i = 0; i < inside.length; i++) {
            var a = (i === 0) ? reg.startS
                              : (inside[i - 1].endS + inside[i].startS) / 2;
            var b = (i === inside.length - 1) ? reg.endS
                              : (inside[i].endS + inside[i + 1].startS) / 2;
            if (b <= a) continue;
            var sh = shiftFor(freqToMidi(inside[i].medianFreqHz));
            // A tile boundary only exists so the SHIFT can change. Neighbours that
            // land on the same interval come off the same stream, so leaving a
            // boundary between them would crossfade a signal with itself — a +3 dB
            // bump at every such seam. Diatonic intervals repeat constantly (a
            // "3rd above" is only ever +3 or +4), so this merges most of them.
            var prev = local[local.length - 1];
            if (prev && Math.abs(prev.shift - sh) < 1e-6) { prev.endS = b; continue; }
            local.push({ startS: a, endS: b, shift: sh, head: false, tail: false });
        }
        if (!local.length) continue;
        local[0].head = true;
        local[local.length - 1].tail = true;
        for (var q = 0; q < local.length; q++) tiles.push(local[q]);
    }
    return tiles;
}

function nearestNote(notes, atS) {
    var best = null, bestD = Infinity;
    for (var i = 0; i < notes.length; i++) {
        var d = (atS < notes[i].startS) ? notes[i].startS - atS
              : (atS > notes[i].endS ? atS - notes[i].endS : 0);
        if (d < bestD) { bestD = d; best = notes[i]; }
    }
    return best;
}


// Expand a preset (or a custom voice list) into concrete per-voice plans with
// humanization baked in. This is where the "distinct singers, not clones"
// decorrelation lives.
function buildVoicePlan(analysis, cfg) {
    cfg = cfg || {};
    var scaleName = cfg.scale || (analysis.key.mode === 'minor' ? 'natural_minor' : 'major');
    var scale = SCALES[scaleName] || SCALES.major;
    var tonic = (typeof cfg.tonic === 'number') ? cfg.tonic : analysis.key.tonic;
    var mode = cfg.mode || 'diatonic';                 // 'diatonic' | 'fixed'
    var humanize = clamp(typeof cfg.humanize === 'number' ? cfg.humanize : 0.42, 0, 1);
    var seed = (cfg.seed || 0x9e3779b1) >>> 0;
    var segs = analysis.segments || [];
    // Older analyses (or a caller that built one by hand) have no gate — fall
    // back to the note segments so the plan is still renderable.
    var voiced = (analysis.voiced && analysis.voiced.length)
        ? analysis.voiced
        : segs.map(function (g) { return { startS: g.startS, endS: g.endS }; });

    var voiceDefs = cfg.voices;
    if (!voiceDefs) {
        var preset = null, id = cfg.preset || 'trio';
        for (var p = 0; p < PRESETS.length; p++) if (PRESETS[p].id === id) preset = PRESETS[p];
        preset = preset || PRESETS[2];
        if (typeof cfg.humanize !== 'number') humanize = preset.humanize;
        if (preset.choir) {
            voiceDefs = buildChoirVoices(preset.choirVoices || 10);
        } else {
            voiceDefs = preset.voices;
        }
    }

    var voices = voiceDefs.map(function (v, vi) {
        var rnd = mulberry32((seed + vi * 0x9E3779B1) >>> 0);
        var isChoir = !!cfg.choir || (voiceDefs.length >= 6);
        var sign = (vi % 2 === 0) ? 1 : -1;

        // Detune (cents): opposite signs across voices + jitter. Choir spreads wider
        // than a double, but not by much — the point is decorrelation, not
        // detuning. The choir range was 16-46 cents until 2026-08-22, which put
        // two "unison" voices up to 63 cents apart: a third of a semitone, and
        // right on the psychoacoustic roughness peak for their upper partials.
        // (Nobody heard it while the voicing gate kept the choir silent 93% of
        // the time.) Vassilakis roughness measured on three takes — a synthetic
        // soprano, a male take, a real separated stem — falls 17-27% at this
        // range, while inter-voice correlation stays near 0.5, far from the ~1.0
        // where layered copies collapse into one comb-filtered clone. Timing,
        // formant and pan spread still do the "distinct singers" work.
        var detMag = isChoir ? (6 + rnd() * 12) : (4 + rnd() * 4);
        var detuneCents = humanize * detMag * sign * (0.7 + rnd() * 0.6);

        // Micro-timing (ms): kept < Haas 30ms so a double thickens, not echoes.
        var timingMs = humanize * (isChoir ? (4 + rnd() * 22) : (3 + rnd() * 16));
        timingMs = clamp(timingMs, 0, 28);

        // Per-voice formant nudge — cheap "different singer" without AI.
        var formant = (v.formant || 0) + humanize * (rnd() * 0.6 - 0.3);

        // Pan: base + spread; low/bass voices pulled toward center (mono-safe lows).
        var isLow = (typeof v.fixed === 'number' && v.fixed <= -12) || v.degree < 0;
        var spread = isChoir ? 0.9 : 0.45;
        var pan = clamp((v.pan || 0) + spread * (rnd() * 0.4 - 0.2), -1, 1);
        if (isLow) pan *= 0.4;

        var gainDb = (typeof v.gainDb === 'number' ? v.gainDb : -4) + humanize * (rnd() * 3 - 1.5);

        // Where this voice sounds, and by how much it is shifted there. The GATE
        // (voiced regions) decides WHERE; the notes decide only BY HOW MUCH, and
        // only for diatonic voices. Fixed / octave / choir voices skip the notes
        // entirely — asking them for a pitch they never use is what used to make
        // an untrackable note silent instead of merely un-harmonised.
        var planSegs;
        if (typeof v.fixed === 'number' || mode === 'fixed') {
            var flat = (typeof v.fixed === 'number')
                ? v.fixed                                       // octave/effect voice
                : degreeToFixedSemitones(v.degree || 0);        // fixed-interval mode
            flat += detuneCents / 100;
            planSegs = voiced.map(function (r) {
                return { startS: r.startS, endS: r.endS, shift: flat, head: true, tail: true };
            });
        } else {
            planSegs = tileRegions(voiced, segs, function (noteMidi) {
                // Interval-preserving diatonic transposition: snap the LEAD to the
                // scale, take the whole-semitone interval from there, and apply that
                // to the untouched voice. The harmony then tracks the singer's own
                // vibrato and intonation instead of being auto-tuned onto the grid
                // (which beats against an expressive lead). It is also what makes
                // the shift set tiny — a "3rd above" is +3 or +4 and nothing else —
                // so a voice renders as two full-length streams, not N note slices.
                var idx = snapIndex(noteMidi, tonic, scale);
                var lead = midiFromScaleIndex(idx, tonic, scale);
                var target = midiFromScaleIndex(idx + (v.degree || 0), tonic, scale);
                return (target - lead) + detuneCents / 100;
            });
        }

        return {
            label: v.label || ('Voice ' + (vi + 1)),
            segments: planSegs,
            formant: formant,
            pan: pan,
            gainDb: gainDb,
            timingMs: timingMs,
            enabled: v.enabled !== false
        };
    });

    return { voices: voices, scaleName: scaleName, tonic: tonic, mode: mode, humanize: humanize };
}

// Procedural choir: N voices split across unison / ±octave with wide detune.
function buildChoirVoices(n) {
    var out = [];
    var slots = [0, 0, 12, -12, 0, 12, -12, 0, 12, -12, 0, -12, 12, 0, 12, -12];
    for (var i = 0; i < n; i++) {
        var fixed = slots[i % slots.length];
        out.push({
            label: (fixed === 0 ? 'Unison ' : (fixed > 0 ? 'Oct↑ ' : 'Oct↓ ')) + (i + 1),
            fixed: fixed,
            gainDb: -6 - (i % 3),
            pan: 0,                          // spread comes from humanization
            formant: (i % 2 ? 0.4 : -0.4)
        });
    }
    return out;
}

/* ============================ rendering ============================ */

// Render one voice to a full-length mono wet buffer, silent outside the voiced
// regions.
//
// A voice's tiles carry only a handful of DISTINCT shifts — an octave voice has
// one, a diatonic "3rd above" has two (+3 and +4), and the bound is the scale
// length because the interval is whole-semitone. So the voice is rendered as one
// full-length pitch-shifted STREAM per distinct shift, and the wet buffer is the
// envelope-weighted sum of those streams.
//
// This replaced a per-note slice-and-shift path. Slicing meant each note was
// pitched in isolation: a fresh engine reset and latency prime per note, a hard
// 0.12 s floor under which a note was silently dropped, and a fade to zero at
// every note boundary. Streams have none of that — one continuous pass, no
// minimum note length, and legato note changes crossfade instead of dipping.
// Peak memory is unchanged: streams are rendered and released one at a time.
async function renderVoice(sourceMono, voicePlan, opts) {
    opts = opts || {};
    var onp = opts.onProgress || function () {};
    var signal = opts.signal || null;
    var sr = sourceMono.sampleRate;
    var wet = makeBuffer(1, sourceMono.length, sr);
    var out = wet.getChannelData(0);
    var tiles = voicePlan.segments;
    if (!tiles || !tiles.length) { onp(1); return wet; }

    // Group tiles by shift (1/1000 semitone is well below audibility).
    var order = [], byKey = {};
    for (var i = 0; i < tiles.length; i++) {
        var key = tiles[i].shift.toFixed(3);
        if (!byKey[key]) { byKey[key] = []; order.push(key); }
        byKey[key].push(tiles[i]);
    }

    var edgeS = Math.max(1, Math.round(EDGE_FADE_S * sr));   // anti-click at region edges
    // Even, so the two halves either side of a boundary are exact complements.
    var xfadeS = 2 * Math.max(1, Math.round(XFADE_S * sr / 2)); // legato note change inside a region

    for (var k = 0; k < order.length; k++) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var group = byKey[order[k]];
        var stream;
        try {
            stream = await shiftBuffer(sourceMono, parseFloat(order[k]), voicePlan.formant, signal);
        } catch (err) {
            if (err && err.name === 'AbortError') throw err;
            onp((k + 1) / order.length);
            continue;                       // lose this interval, not the whole voice
        }
        var sd = stream.getChannelData(0);
        for (var t = 0; t < group.length; t++) writeTile(out, sd, group[t], sr, edgeS, xfadeS);
        stream = null; sd = null;           // release before the next stream is allocated
        onp((k + 1) / order.length);
    }
    return wet;
}

var EDGE_FADE_S = 0.006;   // region in/out — just enough to kill the click
var XFADE_S     = 0.024;   // note-to-note inside a region; linear halves sum to 1

// Add one tile of a (time-aligned, rate-1) shifted stream into the wet buffer.
//
// `head`/`tail` mark the tile as touching the edge of its voiced region. A
// region edge fades from/to silence, so a short LINEAR anti-click ramp is right.
// An internal note boundary is different: the tile is widened by half a
// crossfade on each side and hands over to its neighbour there — and the two
// neighbours come from DIFFERENT pitch-shifted streams, which are uncorrelated.
// Complementary linear gains would then sum to sqrt(a^2+(1-a)^2) = -3 dB at the
// midpoint (measured worse, -9 dB, once partial cancellation is included), an
// audible thinning on every legato note change. Equal-power (sin/cos) gains keep
// the sum of SQUARES at 1, which is the correct law for uncorrelated sources.
function writeTile(out, src, tile, sr, edgeS, xfadeS) {
    var half = xfadeS >> 1;
    var a = Math.round(tile.startS * sr) - (tile.head ? 0 : half);
    var b = Math.round(tile.endS * sr) + (tile.tail ? 0 : half);
    a = clamp(a, 0, out.length);
    b = clamp(b, 0, Math.min(out.length, src.length));
    var len = b - a;
    if (len <= 0) return;
    var fin = Math.min(tile.head ? edgeS : xfadeS, len);
    var fout = Math.min(tile.tail ? edgeS : xfadeS, len);
    if (fin + fout > len) {
        var sc = len / (fin + fout);
        fin = Math.max(1, Math.floor(fin * sc));
        fout = Math.max(1, Math.floor(fout * sc));
    }
    var HALF_PI = Math.PI / 2;
    for (var i = 0; i < len; i++) {
        var w = 1;
        if (i < fin) {
            var xi = (i + 1) / (fin + 1);
            w = tile.head ? xi : Math.sin(HALF_PI * xi);
        } else if (len - i <= fout) {
            var xo = (len - i) / (fout + 1);
            w = tile.tail ? xo : Math.sin(HALF_PI * xo);
        }
        out[a + i] += src[a + i] * w;
    }
}

// Pitch-shift a mono buffer by `semitones` (rate 1, formant-preserved). Prefers
// the off-main-thread Signalsmith worker (no <1s→FFmpeg reroute); falls back to
// AudioPipeline.stretch (adds AudioWorklet + FFmpeg floor) if the worker path
// is unavailable (RU proxy / AV scrub).
async function shiftBuffer(buf, semitones, formantSemitones, signal) {
    var o = {
        semitones: semitones,
        rate: 1,
        preserveFormants: true,
        formantSemitones: formantSemitones || 0,
        // голоса гармонии строятся из спетой партии — музыка: вокодер, как раньше
        content: 'music',
        slug: 'harmony-generator',
        signal: signal || undefined
    };
    if (window.SignalsmithWorkerEngine && window.SignalsmithWorkerEngine.isAvailable && window.SignalsmithWorkerEngine.isAvailable()) {
        try { return await window.SignalsmithWorkerEngine.shift(buf, o); }
        catch (e) { if (e && e.name === 'AbortError') throw e; /* fall through */ }
    }
    return await window.AudioPipeline.stretch(buf, o);
}

// Orchestrate a full harmony render: analyze (if not provided), plan, then
// render each voice SEQUENTIALLY (the Signalsmith worker serializes anyway and
// this bounds peak memory to one voice at a time). Aggregates progress so voice
// i occupies [i/N, (i+1)/N] of the bar.
async function render(sourceBuffer, cfg, opts) {
    opts = opts || {};
    var onp = opts.onProgress || function () {};
    var signal = opts.signal || null;

    var analysis = opts.analysis;
    if (!analysis) {
        analysis = await analyze(sourceBuffer, { onProgress: function (p) { onp(p * 0.15, 'analyze'); } });
    }
    var plan = buildVoicePlan(analysis, cfg);
    var sourceMono = downmixMono(sourceBuffer);

    var enabled = plan.voices.filter(function (v) { return v.enabled; });
    var N = enabled.length || 1;
    var results = [];
    var base = analysis === opts.analysis ? 0 : 0.15;
    var span = 1 - base;

    for (var i = 0; i < enabled.length; i++) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var vp = enabled[i];
        var lo = base + span * (i / N);
        var hi = base + span * ((i + 1) / N);
        // Per-voice memory pre-flight (N voices multiply peak RAM).
        try {
            if (window.AudioPipeline && window.AudioPipeline.isStretchSafe &&
                !window.AudioPipeline.isStretchSafe(sourceMono, { rate: 1 })) {
                throw makeTooLargeError();
            }
        } catch (e) { if (e && e.code === 'AUDIO_TOO_LARGE') throw e; }

        var wet = await renderVoice(sourceMono, vp, {
            signal: signal,
            onProgress: function (p) { onp(lo + (hi - lo) * p, 'render'); }
        });
        results.push({ buffer: wet, params: vp, label: vp.label });
        onp(hi, 'render');
    }

    return { voices: results, analysis: analysis, plan: plan };
}

function makeTooLargeError() {
    var ErrClass = (window.ToolErrors && window.ToolErrors.FileTooLargeError) || Error;
    var err = new ErrClass({ size: 0, name: 'audio' }, 0);
    err.code = 'AUDIO_TOO_LARGE';
    err.message = 'Audio too large for in-browser harmony on this device. Try a shorter clip.';
    return err;
}

/* ============================ mixing / preview ============================ */

// Stereo panner with a fallback for browsers without StereoPannerNode
// (iOS Safari 14.0–14.4, some old WebViews). Returns {input, output}. The
// fallback splits a signal into equal-power L/R gains through a 2-ch merger.
function makePanner(ctx, pan) {
    if (typeof ctx.createStereoPanner === 'function') {
        var sp = ctx.createStereoPanner();
        sp.pan.value = pan;
        return { input: sp, output: sp };
    }
    var theta = (pan + 1) * 0.25 * Math.PI;   // 0..pi/2 across L..R
    var input = ctx.createGain();
    var lg = ctx.createGain(); lg.gain.value = Math.cos(theta);
    var rg = ctx.createGain(); rg.gain.value = Math.sin(theta);
    var merger = ctx.createChannelMerger(2);
    input.connect(lg); input.connect(rg);
    lg.connect(merger, 0, 0);
    rg.connect(merger, 0, 1);
    return { input: input, output: merger };
}

// Build a per-voice node chain (gain + light 3-band EQ + pan + micro-delay).
// ONE function used by BOTH the offline mixdown and the live A/B preview so the
// two never drift (the audio-mixer's classic maintainability wart).
function buildVoiceChain(ctx, buffer, params, width) {
    var src = ctx.createBufferSource();
    src.buffer = buffer;
    if (typeof width !== 'number') width = 1;

    // Light air/warmth EQ so stacked voices sit around the lead.
    var hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 90; hp.Q.value = 0.7;
    var lowShelf = ctx.createBiquadFilter(); lowShelf.type = 'lowshelf'; lowShelf.frequency.value = 200; lowShelf.gain.value = params.eqLow || -1.5;
    var presence = ctx.createBiquadFilter(); presence.type = 'peaking'; presence.frequency.value = 3000; presence.Q.value = 0.9; presence.gain.value = params.eqPresence || 1.0;
    var air = ctx.createBiquadFilter(); air.type = 'highshelf'; air.frequency.value = 8000; air.gain.value = params.eqAir || 1.5;

    var delay = ctx.createDelay(0.05);
    delay.delayTime.value = Math.min(0.049, (params.timingMs || 0) / 1000);

    var pan = makePanner(ctx, clamp((params.pan || 0) * width, -1, 1));

    var gain = ctx.createGain();
    gain.gain.value = dbToGain(typeof params.gainDb === 'number' ? params.gainDb : -4);

    src.connect(hp); hp.connect(lowShelf); lowShelf.connect(presence);
    presence.connect(air); air.connect(delay); delay.connect(pan.input); pan.output.connect(gain);
    return { source: src, output: gain, gainNode: gain };
}

// Generate an exponential-decay noise impulse response for the reverb "Space".
function makeImpulse(ctx, seconds, decay) {
    var rate = ctx.sampleRate, len = Math.max(1, Math.floor(rate * seconds));
    var imp = ctx.createBuffer(2, len, rate);
    for (var c = 0; c < 2; c++) {
        var d = imp.getChannelData(c);
        for (var i = 0; i < len; i++) {
            d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
        }
    }
    return imp;
}

// Offline mixdown: dry lead (full) + enabled wet voices → master limiter.
//   opts.includeVoices (bool[]) — export honors per-voice mute.
//   opts.width (0..2, default 1) — scales each harmony voice's stereo pan.
//   opts.space (0..1, default 0) — gentle reverb glue on the HARMONY bus only
//     (lead stays dry/present) so the stack sounds produced, not raw-summed.
async function mixToBuffer(dryBuffer, voices, opts) {
    opts = opts || {};
    var sr = dryBuffer.sampleRate;
    var length = dryBuffer.length;
    var wetGain = typeof opts.wet === 'number' ? opts.wet : 0.85;
    var dryGain = typeof opts.dry === 'number' ? opts.dry : 1.0;
    var width = typeof opts.width === 'number' ? opts.width : 1;
    var space = clamp(typeof opts.space === 'number' ? opts.space : 0, 0, 1);
    var include = opts.includeVoices || null;

    // Мастер-лимитер держит упреждение 6 мс: при длине = исходнику микс
    // опаздывал от исходного голоса на 288 отсчётов (48 кГц) и терял свой хвост —
    // а дорожку гармоний кладут в DAW поверх оригинала. Рендер длиннее на
    // задержку, голова отрезается (NodeLatency, 2026-10-09).
    var lead = window.NodeLatency ? await window.NodeLatency.compressor(sr) : 0;
    var ctx = new OfflineAudioContext(2, length + lead, sr);

    var drySrc = ctx.createBufferSource(); drySrc.buffer = dryBuffer;
    var dryG = ctx.createGain(); dryG.gain.value = dryGain;
    drySrc.connect(dryG);

    var wetBus = ctx.createGain(); wetBus.gain.value = wetGain;

    var starts = [drySrc];
    for (var i = 0; i < voices.length; i++) {
        // `includeVoices`, when supplied, is authoritative (it already folds in
        // mute AND solo); otherwise fall back to the voice's own enabled flag.
        if (include) { if (include[i] === false) continue; }
        else if (voices[i].params && voices[i].params.enabled === false) continue;
        var chain = buildVoiceChain(ctx, voices[i].buffer, voices[i].params, width);
        chain.output.connect(wetBus);
        starts.push(chain.source);
    }

    // Master limiter — 4 stacked voices clip easily.
    var limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -3; limiter.knee.value = 0; limiter.ratio.value = 20;
    limiter.attack.value = 0.003; limiter.release.value = 0.25;

    dryG.connect(limiter); wetBus.connect(limiter);

    // Reverb send on the harmony bus (glue). Pre-delayed a touch so onsets stay
    // clear; scaled by `space`. Skipped entirely at space≈0 (no CPU cost).
    if (space > 0.001) {
        var conv = ctx.createConvolver();
        conv.buffer = makeImpulse(ctx, 1.8, 2.4);
        var preDelay = ctx.createDelay(0.05); preDelay.delayTime.value = 0.02;
        var revGain = ctx.createGain(); revGain.gain.value = space * 0.85;
        wetBus.connect(preDelay); preDelay.connect(conv); conv.connect(revGain); revGain.connect(limiter);
    }

    limiter.connect(ctx.destination);

    for (var s = 0; s < starts.length; s++) starts[s].start(0);
    var rendered = await ctx.startRendering();
    return lead ? window.NodeLatency.trimHead(rendered, lead, length) : rendered;
}

/* ============================ export ============================ */

window.HarmonyEngine = {
    KEYS: KEYS,
    SCALES: SCALES,
    PRESETS: PRESETS,
    detectKey: detectKey,
    keyFromFrames: keyFromFrames,
    analyze: analyze,
    buildVoicePlan: buildVoicePlan,
    render: render,
    renderVoice: renderVoice,
    buildVoiceChain: buildVoiceChain,
    mixToBuffer: mixToBuffer,
    // low-level helpers exposed for tests / advanced callers
    diatonicTranspose: diatonicTranspose,
    freqToMidi: freqToMidi,
    midiToFreq: midiToFreq,
    downmixMono: downmixMono,
    version: '1.0.0'
};

})();
